import React, { Component } from 'react';
import {
	Box,
	Button,
	Stack,
	Paper,
	Typography,
	Alert,
	AlertTitle,
	CircularProgress,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Divider,
	Card,
	CardContent,
	Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DevicesIcon from '@mui/icons-material/Devices';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import BlockIcon from '@mui/icons-material/Block';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import PaymentIcon from '@mui/icons-material/Payment';
import { post } from '../util/api';
import { connect } from 'react-redux';

class PaddlePaymentPage extends Component {
	state = {
		loading: true,
		processing: false,
		error: null,
		success: false,
		productId: null,
		activeStep: 0,
		showTroubleshootingSteps: false,
		login_user: this.props.user,
	};

	componentDidMount() {
		// Get product ID from URL
		const pathParts = window.location.pathname.split('/');
		const productId = pathParts[pathParts.length - 1];

		if (!productId || isNaN(productId)) {
			this.setState({
				loading: false,
				error: 'Invalid product ID in URL',
			});
			return;
		}

		this.setState({ productId }, () => {
			this.setupPaddleMessageListener();
			this.initializePaddleCheckout();
		});
	}

	componentWillUnmount() {
		window.removeEventListener('message', this.handlePaddleMessage);
	}

	setupPaddleMessageListener = () => {
		window.addEventListener('message', this.handlePaddleMessage);
	};

	handlePaddleMessage = (event) => {
		console.log('Paddle message received:', event);

		if (event && event.data && event.data.callback_data) {
			const { error, checkout, user } = event.data.callback_data;

			if (error) {
				this.setState({
					error: error,
					processing: false,
					showTroubleshootingSteps: true,
				});

				// Send footprint for error
				post('/footprint/add', {
					category: 'maytapi',
					label: 'paddle-payment-declined',
					extra: {
						productId: this.state.productId,
						checkout: checkout,
						login_user: this.state.login_user,
						user: user,
						error: error,
					},
				}).catch(console.error);
			}
		}
	};

	initializePaddleCheckout = async () => {
		this.setState({ loading: true });

		try {
			const { productId } = this.state;

			// Get custom paddle link
			const response = await post('/maytapi/custompaddlelink', {
				plan_id: productId,
				isVat: false,
				country: 'BR',
				email: '',
				date: new Date(),
			});

			if (response && response.data && response.data.response && response.data.response.url) {
				this.setState({ loading: false });

				// Open Paddle checkout
				window.Paddle.Checkout.open({
					override: response.data.response.url,
					successCallback: this.handleSuccess,
					closeCallback: this.handleClose,
				});
			} else {
				throw new Error('Failed to get payment link');
			}
		} catch (err) {
			console.error('Checkout initialization error:', err);
			this.setState({
				loading: false,
				error: 'Failed to initialize payment. Please try again.',
			});

			// Send footprint for initialization error
			post('/footprint/add', {
				category: 'maytapi',
				label: 'paddle-checkout-init-error',
				extra: {
					productId: this.state.productId,
					login_user: this.state.login_user,
					error: err.message,
				},
			}).catch(console.error);
		}
	};

	handleSuccess = (data) => {
		console.log('Payment success:', data);

		this.setState({
			success: true,
			processing: false,
			error: null,
		});

		// Send success footprint
		post('/footprint/add', {
			category: 'maytapi',
			label: 'checkout-success',
			extra: {
				productId: this.state.productId,
				login_user: this.state.login_user,
				checkout: data.checkout,
				user: data.user,
			},
		}).catch(console.error);
	};

	handleClose = (data) => {
		console.log('Checkout closed:', data);

		if (!this.state.success) {
			this.setState({
				processing: false,
			});

			// Send close footprint
			post('/footprint/add', {
				category: 'maytapi',
				label: 'checkout-close',
				extra: {
					productId: this.state.productId,
					login_user: this.state.login_user,
					checkout: data.checkout,
					user: data.user,
				},
			}).catch(console.error);
		}
	};

	handleRetry = () => {
		this.setState({
			error: null,
			showTroubleshootingSteps: false,
			activeStep: 0,
		});
		this.initializePaddleCheckout();
	};

	render() {
		const { loading, processing, error, success, showTroubleshootingSteps, productId } = this.state;

		const troubleshootingSteps = [
			{
				label: 'Check Firewall Settings',
				icon: <SecurityIcon />,
				description: 'Ensure that "paddle.com" is not blocked by your firewall or security software.',
			},
			{
				label: 'Clear Browser Cache',
				icon: <DeleteSweepIcon />,
				description: 'Clear your browser cache and cookies, then try again.',
			},
			{
				label: 'Try Different Browser or Device',
				icon: <DevicesIcon />,
				description: 'Use a different browser, device, or Wi-Fi connection to complete the payment.',
			},
			{
				label: 'Disconnect VPN',
				icon: <VpnLockIcon />,
				description: 'If you are using a VPN, please disconnect it before re-attempting checkout.',
			},
			{
				label: 'Disable Pop-up Blockers',
				icon: <BlockIcon />,
				description: 'Make sure pop-up blockers are turned off in your browser settings.',
			},
			{
				label: 'Try Different Payment Method',
				icon: <CreditCardIcon />,
				description: 'Try a different card or payment method (e.g., PayPal, Apple Pay, Google Pay).',
			},
		];

		const paymentMethods = [
			{
				name: 'Credit or Debit Card',
				icon: <CreditCardIcon sx={{ fontSize: 28 }} />,
				note: null,
			},
			{
				name: 'PayPal',
				icon: <PaymentIcon sx={{ fontSize: 28 }} />,
				note: 'Not available in India',
			},
			{
				name: 'Apple Pay',
				icon: <AppleIcon sx={{ fontSize: 28 }} />,
				note: 'Safari only',
			},
			{
				name: 'Google Pay',
				icon: <GoogleIcon sx={{ fontSize: 28 }} />,
				note: 'Chrome only, outside India',
			},
			{
				name: 'Wise Transfer',
				icon: <AccountBalanceIcon sx={{ fontSize: 28 }} />,
				note: 'Available for transactions over $100',
			},
		];

		if (loading) {
			return (
				<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
					<Paper sx={{ maxWidth: 500, p: 4, textAlign: 'center', boxShadow: 'rgb(131 131 131 / 65%) 0px 2px 8px 0px' }}>
						<CircularProgress size={60} sx={{ mb: 3, color: '#21ba45' }} />
						<Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
							Initializing Payment...
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Please wait while we prepare your checkout
						</Typography>
					</Paper>
				</Box>
			);
		}

		if (success) {
			return (
				<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
					<Paper sx={{ maxWidth: 600, p: 4, textAlign: 'center', boxShadow: 'rgb(131 131 131 / 65%) 0px 2px 8px 0px' }}>
						<CheckCircleIcon sx={{ fontSize: 80, color: '#21ba45', mb: 2 }} />
						<Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
							Payment Successful!
						</Typography>
						<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
							Thank you for your purchase. You will receive a confirmation email shortly.
						</Typography>
						<Button variant="contained" size="large" href="/" sx={{ bgcolor: '#21ba45', '&:hover': { bgcolor: '#1b9f3a' } }}>
							Go to Dashboard
						</Button>
					</Paper>
				</Box>
			);
		}

		return (
			<Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', py: 4 }}>
				<Box sx={{ maxWidth: 900, mx: 'auto', px: 2 }}>
					<Paper sx={{ p: 4, boxShadow: 'rgb(131 131 131 / 65%) 0px 2px 8px 0px' }}>
						{/* Header */}
						<Box sx={{ textAlign: 'center', mb: 4 }}>
							<img src="/maytapi.svg" alt="Maytapi" style={{ width: '10rem', marginBottom: '16px' }} />
							<Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
								Complete Your Purchase
							</Typography>
							<Typography variant="body1" color="text.secondary">
								Product ID: {productId}
							</Typography>
						</Box>

						{/* Error Alert */}
						{error && (
							<Alert severity="error" sx={{ mb: 3 }}>
								<AlertTitle>Payment Failed</AlertTitle>
								{error}
							</Alert>
						)}

						{/* Initial State - No Error */}
						{!error && !showTroubleshootingSteps && (
							<Box sx={{ textAlign: 'center', py: 4 }}>
								<Typography variant="body1" color="text.secondary" gutterBottom>
									The payment window should open automatically.
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									If it doesn't, please click the button below.
								</Typography>
								<Button
									variant="contained"
									size="large"
									onClick={this.handleRetry}
									disabled={processing}
									startIcon={processing ? <CircularProgress size={20} /> : <RefreshIcon />}
									sx={{ bgcolor: '#21ba45', '&:hover': { bgcolor: '#1b9f3a' } }}
								>
									{processing ? 'Processing...' : 'Open Payment Window'}
								</Button>
							</Box>
						)}

						{/* Troubleshooting Steps */}
						{showTroubleshootingSteps && (
							<Box sx={{ mt: 3 }}>
								<Alert severity="info" sx={{ mb: 3 }}>
									<AlertTitle>Payment Issue?</AlertTitle>
									If your payment failed, please try the following steps to resolve the issue.
								</Alert>

								{/* Troubleshooting Checklist */}
								<Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
									Troubleshooting Steps
								</Typography>

								<Card variant="outlined" sx={{ mb: 4 }}>
									<CardContent sx={{ p: 0 }}>
										<List>
											{troubleshootingSteps.map((step, index) => (
												<React.Fragment key={index}>
													<ListItem sx={{ py: 2 }}>
														<ListItemIcon sx={{ color: '#21ba45', minWidth: 48 }}>{step.icon}</ListItemIcon>
														<ListItemText
															primary={
																<Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
																	{step.label}
																</Typography>
															}
															secondary={step.description}
														/>
													</ListItem>
													{index < troubleshootingSteps.length - 1 && <Divider component="li" />}
												</React.Fragment>
											))}
										</List>
									</CardContent>
								</Card>

								{/* Alternative Payment Methods */}
								<Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
									Available Payment Methods
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									You may try again using one of the following payment options:
								</Typography>

								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
										gap: 2,
										mb: 4,
									}}
								>
									{paymentMethods.map((method, index) => (
										<Card
											key={index}
											variant="outlined"
											sx={{
												p: 2,
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												textAlign: 'center',
												transition: 'all 0.2s',
												'&:hover': {
													borderColor: '#21ba45',
													boxShadow: '0 2px 8px rgba(33, 186, 69, 0.15)',
												},
											}}
										>
											<Box sx={{ color: '#21ba45', mb: 1 }}>{method.icon}</Box>
											<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
												{method.name}
											</Typography>
											{method.note && (
												<Chip
													label={method.note}
													size="small"
													sx={{
														fontSize: '0.7rem',
														height: '20px',
														bgcolor: '#f5f5f5',
														color: '#666',
													}}
												/>
											)}
										</Card>
									))}
								</Box>

								<Divider sx={{ my: 3 }} />

								{/* Retry Button */}
								<Box sx={{ textAlign: 'center' }}>
									<Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
										Ready to try again?
									</Typography>
									<Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
										<Button
											variant="contained"
											size="large"
											onClick={this.handleRetry}
											startIcon={<RefreshIcon />}
											sx={{ bgcolor: '#21ba45', '&:hover': { bgcolor: '#1b9f3a' } }}
										>
											Retry Payment
										</Button>
										<Button variant="outlined" href="/" color="error" size="large">
											Back to Home
										</Button>
									</Stack>
								</Box>
							</Box>
						)}
					</Paper>

					{/* Footer */}
					<Box sx={{ textAlign: 'center', mt: 3 }}>
						<Typography variant="body2" color="text.secondary">
							Having trouble? Contact our support team at{' '}
							<a href="mailto:info@maytapi.com" style={{ color: '#21ba45', textDecoration: 'none', fontWeight: 500 }}>
								info@maytapi.com
							</a>
						</Typography>
					</Box>
				</Box>
			</Box>
		);
	}
}

const stateToProps = (state) => ({
	access: state.access,
	location: state.router.location,
	product: state.product.maytapi,
	user: state.access.user.loggedUserObj,
});

export default connect(stateToProps)(PaddlePaymentPage);
